import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { giftFromRow, type CoinGiftRow } from '../utils/coinGift';
import type { CoinGift } from '../types';

/**
 * Coin gifts from the team (notification center, Phase 4; mig 0287).
 *
 * ⚠️ Loaded on their own, NOT through `notification_feed`: the 0.7.4 app pages that
 * view and badges every unread row it returns, so a new kind there would light a
 * badge it cannot show. A person's gifts are few — the newest 50 load at once.
 *
 * ⚠️⚠️ Accepting is the only path from a gift to Coin, and it is the server's:
 * `accept_coin_gift()` checks, locks the wallet with the shared lock and writes
 * one ledger row (external_ref 'gift:<id>' — once, by index). The existing "A gift"
 * toast then fires from that ledger row (useCoinGrants), so there is ONE path
 * from row to card. Nothing here credits anything.
 */
interface GiftState {
  gifts: CoinGift[];
  /** Ids with an accept/decline in flight — the buttons wait on them. */
  busy: Record<string, true>;
  load: () => Promise<void>;
  /** A realtime INSERT/UPDATE for one of my gifts. */
  upsertRow: (row: CoinGiftRow) => void;
  accept: (id: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  decline: (id: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  clearStore: () => void;
}

const COLUMNS = 'id, amount_cp, note, created_at, expires_at, accepted_at, declined_at';

/** The server's refusal, in words a person can act on. */
function friendly(message: string): string {
  if (/expired/i.test(message)) return 'This gift has expired.';
  if (/declined/i.test(message)) return 'You declined this gift.';
  if (/already accepted/i.test(message)) return 'You already accepted this gift.';
  if (/not found/i.test(message)) return 'This gift is no longer available.';
  return 'Something went wrong — please try again.';
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useGiftStore = create<GiftState>()((set, get) => ({
  gifts: [],
  busy: {},

  load: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      const { data, error } = await supabase
        .from('coin_gifts')
        .select(COLUMNS)
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (at !== epoch) return; // signed out while it read: not the next account's gifts
      if (error) throw error;
      set({ gifts: ((data ?? []) as CoinGiftRow[]).map(giftFromRow) });
    } catch (error) {
      console.error('Error loading gifts:', error);
    }
  },

  upsertRow: (row) => {
    // a withdrawal reaches nobody (RLS hides the row) — but if one ever arrives, drop it
    if (row.withdrawn_at) {
      set((s) => ({ gifts: s.gifts.filter((g) => g.id !== row.id) }));
      return;
    }
    const gift = giftFromRow(row);
    set((s) => ({
      gifts: s.gifts.some((g) => g.id === gift.id)
        ? s.gifts.map((g) => (g.id === gift.id ? gift : g))
        : [gift, ...s.gifts],
    }));
  },

  accept: async (id) => {
    if (get().busy[id]) return { ok: false, error: 'Already working on it.' };
    set((s) => ({ busy: { ...s.busy, [id]: true } }));
    try {
      const { error } = await supabase.rpc('accept_coin_gift', { p_gift_id: id });
      if (error) return { ok: false, error: friendly(error.message) };
      const now = new Date().toISOString();
      set((s) => ({ gifts: s.gifts.map((g) => (g.id === id ? { ...g, acceptedAt: g.acceptedAt ?? now } : g)) }));
      return { ok: true };
    } catch {
      return { ok: false, error: 'Something went wrong — please try again.' };
    } finally {
      set((s) => {
        const busy = { ...s.busy };
        delete busy[id];
        return { busy };
      });
    }
  },

  decline: async (id) => {
    if (get().busy[id]) return { ok: false, error: 'Already working on it.' };
    set((s) => ({ busy: { ...s.busy, [id]: true } }));
    try {
      const { error } = await supabase.rpc('decline_coin_gift', { p_gift_id: id });
      if (error) return { ok: false, error: friendly(error.message) };
      const now = new Date().toISOString();
      set((s) => ({ gifts: s.gifts.map((g) => (g.id === id ? { ...g, declinedAt: g.declinedAt ?? now } : g)) }));
      return { ok: true };
    } catch {
      return { ok: false, error: 'Something went wrong — please try again.' };
    } finally {
      set((s) => {
        const busy = { ...s.busy };
        delete busy[id];
        return { busy };
      });
    }
  },

  /**
   * Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does
   * not reload the page. An accept or decline still in flight maps by id over the emptied
   * list, so it changes nothing here.
   */
  clearStore: () => {
    epoch += 1;
    set({ gifts: [], busy: {} });
  },
}));

onSignOut(() => useGiftStore.getState().clearStore());
